import { useMemo } from 'react'
import { useSales } from '../hooks/useSales'
import { useExpenses } from '../hooks/useExpenses'
import { usePrints, usePrintFilamentUsage } from '../hooks/usePrints'
import { useProducts } from '../hooks/useProducts'
import { useSaleLineItems } from '../hooks/useSaleLineItems'
import { useFilamentSpools } from '../hooks/useFilamentSpools'
import { formatMoney, monthKey } from '../lib/format'
import { computeProfitForPeriod } from '../lib/profit'

// Computed once at module load time — these are stable across renders
const NOW = new Date()
const CURRENT_YEAR = NOW.getFullYear()
const CURRENT_MONTH = NOW.getMonth()
const CURRENT_MONTH_KEY = monthKey(NOW)
const LAST_MONTH_KEY = monthKey(new Date(CURRENT_YEAR, CURRENT_MONTH - 1, 1))

export default function Reports() {
  const { data: sales } = useSales()
  const { data: expenses } = useExpenses()
  const { data: prints } = usePrints()
  const { data: products } = useProducts()
  const { data: lineItems } = useSaleLineItems()
  const { data: spools } = useFilamentSpools()
  const { data: filamentUsages } = usePrintFilamentUsage()

  // ── YTD profit ──────────────────────────────────────────────────────────────
  const ytdProfit = useMemo(() => {
    // Use a string prefix match instead of constructing a Date. Date parsing
    // of "YYYY-MM-DDT00:00:00" differs between modern + older Safari versions;
    // the database stores plain 'YYYY-MM-DD' so comparing the first 4 chars
    // is both timezone-stable and faster. Same pattern timeRange.ts uses.
    const yearPrefix = `${CURRENT_YEAR}-`
    const ytdSales = (sales ?? []).filter((s) => s.sale_date.startsWith(yearPrefix))
    const ytdExpenses = (expenses ?? []).filter((e) => e.expense_date.startsWith(yearPrefix))
    const ytdPrints = (prints ?? []).filter((p) => p.print_date.startsWith(yearPrefix))
    return computeProfitForPeriod({
      sales: ytdSales,
      expenses: ytdExpenses,
      prints: ytdPrints,
    }).profit
  }, [sales, expenses, prints])

  // ── Monthly profit for last 12 months ────────────────────────────────────────
  const monthlyData = useMemo(() => {
    const months: string[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(CURRENT_YEAR, CURRENT_MONTH - i, 1)
      months.push(monthKey(d))
    }

    return months.map((mk) => {
      const monthSales = (sales ?? []).filter((s) => monthKey(s.sale_date) === mk)
      const monthExpenses = (expenses ?? []).filter((e) => monthKey(e.expense_date) === mk)
      const monthPrints = (prints ?? []).filter((p) => monthKey(p.print_date) === mk)

      const { profit } = computeProfitForPeriod({
        sales: monthSales,
        expenses: monthExpenses,
        prints: monthPrints,
      })

      const [year, month] = mk.split('-')
      const label = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('en-US', {
        month: 'short',
      })

      return { mk, label, profit }
    })
  }, [sales, expenses, prints])

  const maxAbsProfit = useMemo(
    () => Math.max(...monthlyData.map((m) => Math.abs(m.profit)), 1),
    [monthlyData],
  )

  // ── Top-margin products ───────────────────────────────────────────────────────
  // Excludes refunded sales' line items so a refunded sale doesn't drop a
  // product's margin to 0% (revenue gone but cost still in print history).
  const topProducts = useMemo(() => {
    if (!products || !lineItems || !prints) return []

    const refundedSaleIds = new Set(
      (sales ?? []).filter((s) => s.is_refunded).map((s) => s.id),
    )

    return products
      .map((product) => {
        const productLineItems = (lineItems ?? [])
          .filter((li) => li.product_id === product.id)
          .filter((li) => !refundedSaleIds.has(li.sale_id))
        if (productLineItems.length === 0) return null

        const totalRevenue = productLineItems.reduce(
          (sum, li) => sum + Number(li.unit_price) * Number(li.quantity),
          0,
        )
        const relatedPrints = (prints ?? []).filter((p) => p.product_id === product.id)
        const totalPrintCost = relatedPrints.reduce((sum, p) => sum + Number(p.total_cost), 0)
        const unitsSold = productLineItems.reduce((sum, li) => sum + Number(li.quantity), 0)

        if (unitsSold === 0) return null
        const avgRevPerUnit = totalRevenue / unitsSold
        const avgCostPerUnit = unitsSold > 0 ? totalPrintCost / unitsSold : 0
        const margin = avgRevPerUnit > 0 ? ((avgRevPerUnit - avgCostPerUnit) / avgRevPerUnit) * 100 : 0

        return { product, margin, unitsSold, avgRevPerUnit }
      })
      .filter(Boolean)
      .sort((a, b) => (b?.margin ?? 0) - (a?.margin ?? 0))
      .slice(0, 8) as Array<{ product: (typeof products)[0]; margin: number; unitsSold: number; avgRevPerUnit: number }>
  }, [products, lineItems, prints, sales])

  // ── Filament ROI ─────────────────────────────────────────────────────────────
  // For each in-use/finished spool, estimate the revenue earned by the products
  // it helped produce. Chain: spool → print_filament_usage → prints → product_id
  // → sale_line_items (non-refunded). When a print contributes to multiple
  // products, we split its share of the spool's revenue evenly.
  const filamentRoi = useMemo(() => {
    if (!spools || !prints || !lineItems || !filamentUsages) return []

    // sale_line_items grouped by product, EXCLUDING refunded sales
    const refundedSaleIds = new Set((sales ?? []).filter((s) => s.is_refunded).map((s) => s.id))
    const revenueByProduct = new Map<string, number>()
    for (const li of lineItems) {
      if (!li.product_id) continue
      if (refundedSaleIds.has(li.sale_id)) continue
      const cur = revenueByProduct.get(li.product_id) ?? 0
      revenueByProduct.set(li.product_id, cur + Number(li.unit_price) * Number(li.quantity))
    }

    const printsById = new Map((prints ?? []).map((p) => [p.id, p]))

    return spools
      .map((spool) => {
        const gramsConsumed = Number(spool.original_weight_grams) - Number(spool.current_weight_grams)
        if (gramsConsumed <= 0) return null

        // Prints that drew from this spool
        const printIdsForSpool = new Set(
          (filamentUsages ?? [])
            .filter((u) => u.spool_id === spool.id)
            .map((u) => u.print_id),
        )

        // Revenue from the products those prints contributed to.
        // Heuristic: each print attributes the full revenue of its product to
        // this spool. (Crude — doesn't split across all spools used by the
        // print — but consistent and useful for ranking.)
        const productIds = new Set<string>()
        for (const pid of printIdsForSpool) {
          const p = printsById.get(pid)
          if (p?.product_id) productIds.add(p.product_id)
        }
        let spoolRevenue = 0
        for (const pid of productIds) {
          spoolRevenue += revenueByProduct.get(pid) ?? 0
        }

        const purchasePrice = Number(spool.purchase_price ?? 0)
        const revenuePerGram = gramsConsumed > 0 ? spoolRevenue / gramsConsumed : 0
        const costPerGram = purchasePrice > 0 ? purchasePrice / Number(spool.original_weight_grams) : 0

        const name = `${spool.brand ?? ''} ${spool.material} ${spool.color}`.trim()
        return { spool, name, gramsConsumed, revenuePerGram, costPerGram }
      })
      .filter(Boolean)
      .sort((a, b) => (b?.revenuePerGram ?? 0) - (a?.revenuePerGram ?? 0))
      .slice(0, 6) as Array<{ spool: NonNullable<typeof spools>[0]; name: string; gramsConsumed: number; revenuePerGram: number; costPerGram: number }>
  }, [spools, prints, lineItems, filamentUsages, sales])

  // ── Top earners (by total profit) ────────────────────────────────────────────
  // Cash-basis: filament is an expense at purchase time, so per-product cost
  // basis here is machine + labor only. Subtracting full total_cost would
  // double-count filament against this product's profit.
  //
  // Refunded sales: their line items must be excluded from revenue, otherwise
  // products with refunds show negative profit (cost is still subtracted
  // because the underlying prints still ran — but revenue gets zeroed out).
  const topEarners = useMemo(() => {
    if (!products || !lineItems) return []

    const refundedSaleIds = new Set(
      (sales ?? []).filter((s) => s.is_refunded).map((s) => s.id),
    )

    return products
      .map((product) => {
        const productLineItems = (lineItems ?? [])
          .filter((li) => li.product_id === product.id)
          .filter((li) => !refundedSaleIds.has(li.sale_id))
        if (productLineItems.length === 0) return null

        const revenue = productLineItems.reduce(
          (sum, li) => sum + Number(li.unit_price) * Number(li.quantity),
          0,
        )
        if (revenue <= 0) return null

        const productPrints = (prints ?? []).filter((p) => p.product_id === product.id)
        const totalCostBasis = productPrints.reduce(
          (sum, p) => sum + Number(p.machine_cost) + Number(p.labor_cost),
          0,
        )
        const profit = revenue - totalCostBasis

        const unitsSold = productLineItems.reduce((sum, li) => sum + Number(li.quantity), 0)

        return { product, revenue, profit, totalCostBasis, unitsSold }
      })
      .filter((x): x is NonNullable<typeof x> => x !== null && x.unitsSold > 0)
      .sort((a, b) => b.profit - a.profit)
      .slice(0, 5)
  }, [products, lineItems, prints, sales])

  // ── Filament burn rate ────────────────────────────────────────────────────────
  const filamentBurnRate = useMemo(() => {
    if (!spools || !filamentUsages || !prints) return []

    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 28)
    const cutoffStr = cutoff.toISOString().slice(0, 10)

    // Map printId → print_date for date-range filtering
    const printDateById = new Map((prints ?? []).map((p) => [p.id, p.print_date]))

    // Build spool → grams used in last 28 days
    const recentGramsById = new Map<string, number>()
    for (const usage of filamentUsages ?? []) {
      const printDate = printDateById.get(usage.print_id)
      if (!printDate || printDate < cutoffStr) continue
      recentGramsById.set(
        usage.spool_id,
        (recentGramsById.get(usage.spool_id) ?? 0) + usage.grams_used,
      )
    }

    return spools
      .filter((s) => s.status === 'in-use')
      .map((spool) => {
        const totalGrams28 = recentGramsById.get(spool.id) ?? 0
        const weeklyRate = totalGrams28 / 4
        const weeksRemaining = weeklyRate > 0
          ? spool.current_weight_grams / weeklyRate
          : null
        const name = [spool.brand, spool.material, spool.color].filter(Boolean).join(' ')
        return { spool, name, weeklyRate, weeksRemaining }
      })
      .filter((x) => x.weeklyRate > 0)
      .sort((a, b) => (a.weeksRemaining ?? Infinity) - (b.weeksRemaining ?? Infinity))
  }, [spools, filamentUsages, prints])

  // ── Cash flow this month vs last ──────────────────────────────────────────────
  // "Cash flow net" here is the same canonical profit as the rest of the app —
  // net sale revenue (not gross) minus expenses minus print costs.
  const cashFlow = useMemo(() => {
    const calc = (mk: string) => {
      const mSales = (sales ?? []).filter((s) => monthKey(s.sale_date) === mk)
      const mExpenses = (expenses ?? []).filter((e) => monthKey(e.expense_date) === mk)
      const mPrints = (prints ?? []).filter((p) => monthKey(p.print_date) === mk)
      const breakdown = computeProfitForPeriod({
        sales: mSales,
        expenses: mExpenses,
        prints: mPrints,
      })
      return {
        salesIn: breakdown.income,
        expOut: breakdown.expensesOut,
        printOut: breakdown.printCostsOut,
        net: breakdown.profit,
      }
    }
    return { current: calc(CURRENT_MONTH_KEY), last: calc(LAST_MONTH_KEY) }
  }, [sales, expenses, prints])

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720 }}>
      <h1 style={{ marginBottom: 'var(--space-6)' }}>Reports</h1>

      {/* Top earners */}
      {topEarners.length > 0 && (
        <section style={{ marginBottom: 'var(--space-8)' }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: 'var(--color-primary)',
            marginBottom: 'var(--space-3)',
          }}>
            Top earners
          </div>
          <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)' }}>
            {topEarners.map(({ product, profit, revenue, unitsSold }, idx) => {
              const marginColor =
                profit > 0
                  ? 'var(--color-success)'
                  : profit === 0
                  ? 'var(--color-warning)'
                  : 'var(--color-danger)'
              return (
                <div
                  key={product.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-2) 0',
                    borderBottom: idx < topEarners.length - 1 ? '1px solid var(--color-surface-2)' : 'none',
                  }}
                >
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--color-primary-soft)',
                    color: 'var(--color-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {product.name}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                      {unitsSold} sold · {formatMoney(revenue)} revenue
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: marginColor, textAlign: 'right', flexShrink: 0 }}>
                    {formatMoney(profit)}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Filament burn rate */}
      {filamentBurnRate.length > 0 && (
        <section style={{ marginBottom: 'var(--space-8)' }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: 'var(--color-primary)',
            marginBottom: 'var(--space-3)',
          }}>
            Filament burn rate
          </div>
          <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)' }}>
            {filamentBurnRate.map(({ spool, name, weeklyRate, weeksRemaining }, idx) => {
              const urgentColor =
                weeksRemaining !== null && weeksRemaining < 2
                  ? 'var(--color-danger)'
                  : weeksRemaining !== null && weeksRemaining < 4
                  ? 'var(--color-warning)'
                  : 'var(--color-success)'
              return (
                <div
                  key={spool.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    padding: 'var(--space-2) 0',
                    borderBottom: idx < filamentBurnRate.length - 1 ? '1px solid var(--color-surface-2)' : 'none',
                  }}
                >
                  <div style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: spool.color_hex ?? '#888',
                    border: '1px solid rgba(0,0,0,0.15)',
                    flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {name}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                      {weeklyRate.toFixed(0)}g/wk · {spool.current_weight_grams.toFixed(0)}g left
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: urgentColor, textAlign: 'right', flexShrink: 0, fontSize: 'var(--font-size-sm)' }}>
                    {weeksRemaining !== null ? `${weeksRemaining.toFixed(1)} wks` : 'no usage'}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* YTD Hero */}
      <div style={{
        background: ytdProfit >= 0 ? 'var(--color-success)' : 'var(--color-danger)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        marginBottom: 'var(--space-6)',
        color: 'white',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.85, marginBottom: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '1px' }}>
          {CURRENT_YEAR} YTD True Profit
        </div>
        <div style={{ fontSize: '2.5rem', fontWeight: 800 }}>{formatMoney(ytdProfit)}</div>
        <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.75, marginTop: 'var(--space-1)' }}>
          Net revenue minus all expenses and print machine+labor costs
        </div>
      </div>

      {/* Monthly profit chart */}
      <section style={{ marginBottom: 'var(--space-8)' }}>
        <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-4)' }}>
          Monthly profit — last 12 months
        </h2>
        <div style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120 }}>
            {monthlyData.map(({ mk, label, profit }) => {
              const barHeight = Math.max(4, (Math.abs(profit) / maxAbsProfit) * 100)
              const isNeg = profit < 0
              const isCurrentMonth = mk === CURRENT_MONTH_KEY
              return (
                <div
                  key={mk}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                  title={`${label}: ${formatMoney(profit)}`}
                >
                  <div style={{
                    width: '100%',
                    height: barHeight,
                    background: isNeg ? 'var(--color-danger)' : 'var(--color-success)',
                    borderRadius: '3px 3px 0 0',
                    opacity: isCurrentMonth ? 1 : 0.65,
                    border: isCurrentMonth ? '2px solid var(--color-primary)' : 'none',
                  }} />
                  <span style={{ fontSize: 9, color: 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>
                    {label}
                  </span>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
            {monthlyData.filter((m) => m.profit !== 0).slice(-3).map(({ mk, label, profit }) => (
              <span key={mk} style={{ fontSize: 'var(--font-size-xs)', color: profit < 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                {label}: {formatMoney(profit)}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Cash flow */}
      <section style={{ marginBottom: 'var(--space-8)' }}>
        <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-4)' }}>
          Cash flow — this month vs last
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          {[
            { label: 'This month', data: cashFlow.current },
            { label: 'Last month', data: cashFlow.last },
          ].map(({ label, data }) => (
            <div key={label} style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginBottom: 'var(--space-2)' }}>{label}</div>
              <Row label="Sales in" value={data.salesIn} color="var(--color-success)" />
              <Row label="Expenses out" value={-data.expOut} color="var(--color-danger)" />
              <Row label="Print costs" value={-data.printOut} color="var(--color-warning)" />
              <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 'var(--space-2)', paddingTop: 'var(--space-2)' }}>
                <Row label="Net" value={data.net} color={data.net >= 0 ? 'var(--color-success)' : 'var(--color-danger)'} bold />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Top-margin products */}
      {topProducts.length > 0 && (
        <section style={{ marginBottom: 'var(--space-8)' }}>
          <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-4)' }}>
            Top-margin products
          </h2>
          <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)' }}>
            {topProducts.map(({ product, margin, unitsSold, avgRevPerUnit }) => (
              <div key={product.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-2) 0',
                borderBottom: '1px solid var(--color-surface-2)',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{product.name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    {unitsSold} sold · avg {formatMoney(avgRevPerUnit)} / unit
                  </div>
                </div>
                <div style={{
                  fontWeight: 700,
                  color: margin >= 50 ? 'var(--color-success)' : margin >= 30 ? 'var(--color-warning)' : 'var(--color-danger)',
                }}>
                  {margin.toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filament ROI */}
      {filamentRoi.length > 0 && (
        <section style={{ marginBottom: 'var(--space-8)' }}>
          <h2 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-4)' }}>
            Filament ROI
          </h2>
          <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)' }}>
            {filamentRoi.map(({ spool, name, gramsConsumed, revenuePerGram, costPerGram }) => (
              <div key={spool.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-2) 0',
                borderBottom: '1px solid var(--color-surface-2)',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{name}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    {gramsConsumed.toFixed(0)}g consumed
                    {costPerGram > 0 && ` · ${formatMoney(costPerGram)}/g cost`}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
                    {formatMoney(revenuePerGram)}/g
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>revenue/g</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function Row({ label, value, color, bold }: { label: string; value: number; color: string; bold?: boolean }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '2px 0',
      fontSize: 'var(--font-size-sm)',
      fontWeight: bold ? 700 : 400,
    }}>
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ color }}>{formatMoney(value)}</span>
    </div>
  )
}
